import {
  Auth0Provider,
  GenericError,
  MissingRefreshTokenError,
  useAuth0,
} from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { lazy, StrictMode, Suspense, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { setTokenAccessor } from "./api/client";
import { auth0Config } from "./auth/config";
import { FullPageSpinner } from "./components/basics/full-page-spinner";
import { Sentry } from "./lib/sentry";
import { createAppRouter } from "./router";
import "./index.css";

const ReactQueryDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    );

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
    },
  },
});

function InnerApp() {
  const auth = useAuth0();
  const routerRef = useRef<ReturnType<typeof createAppRouter> | null>(null);

  useEffect(() => {
    const getTokenWithRecovery = async () => {
      try {
        return await auth.getAccessTokenSilently();
      } catch (error) {
        if (
          error instanceof MissingRefreshTokenError ||
          (error instanceof GenericError &&
            (error.error === "login_required" ||
              error.error === "invalid_grant"))
        ) {
          await auth.loginWithRedirect({
            appState: { returnTo: window.location.pathname },
          });
        }
        throw error;
      }
    };

    setTokenAccessor(getTokenWithRecovery);
  }, [auth.getAccessTokenSilently, auth.loginWithRedirect]);

  if (auth.isLoading) {
    return <FullPageSpinner />;
  }

  if (!routerRef.current) {
    routerRef.current = createAppRouter(queryClient, {
      isAuthenticated: auth.isAuthenticated,
      getAccessTokenSilently: auth.getAccessTokenSilently,
      loginWithRedirect: auth.loginWithRedirect,
    });
  }

  routerRef.current.update({
    context: {
      queryClient,
      auth: {
        isAuthenticated: auth.isAuthenticated,
        getAccessTokenSilently: auth.getAccessTokenSilently,
        loginWithRedirect: auth.loginWithRedirect,
      },
    },
  });

  return (
    <>
      <RouterProvider router={routerRef.current} />
      <Suspense>
        <ReactQueryDevtools initialIsOpen={false} />
      </Suspense>
    </>
  );
}

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>A critical error occurred.</p>}>
      <Auth0Provider
        {...auth0Config}
        onRedirectCallback={(appState) => {
          window.location.replace(appState?.returnTo ?? "/");
        }}
      >
        <QueryClientProvider client={queryClient}>
          <InnerApp />
        </QueryClientProvider>
      </Auth0Provider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
